// Tenant CRM config (MS-6631, T6.6), as GET /app/ delivers it in `appConfig.data.crm`:
//
//   { provider, requireLeadID, features, providers: [{ provider, displayName, requireLeadID,
//     inbound, outbound, features, source }] }
//
// `provider` is the tenant's CRM of record — the one bookings are delivered to — and it is what the
// tenant-level screens gate on: the sync panel on the overview, the needs_human ops list, the
// walk-in gate copy. It is NOT what the context banner or the per-field provenance tags gate on:
// those describe an active link session and appear for whichever CRM the link came from, so an IJM
// agent arriving from Salesforce sees them on a tenant whose CRM of record is something else, or
// nothing at all. Keeping the two gates apart is the point of this file.
//
// Everything here reads the block defensively. `/app/` is fetched once at boot and the reducer
// stores whatever came back; an older API without the block, or a malformed one, must mean "no
// CRM" and never a crash on a page that has nothing to do with CRMs.

import { providerLabel } from './crmContext';

export const OUTPERFORM = 'outperform';

function emptyCRM() {
  return {
    provider: '', requireLeadID: false, leadRequiredBy: '', features: {}, providers: [], salesSources: [],
  };
}

function isObject(v) {
  return Boolean(v) && typeof v === 'object' && !Array.isArray(v);
}

// tenantCRM normalises the block. Absent, null or malformed pieces read as their empty value.
export function tenantCRM(appConfig) {
  const crm = appConfig && appConfig.data && appConfig.data.crm;
  if (!isObject(crm)) return emptyCRM();
  const named = p => isObject(p) && typeof p.provider === 'string' && p.provider.trim() !== '';
  const providers = Array.isArray(crm.providers) ? crm.providers.filter(named) : [];
  return {
    provider: typeof crm.provider === 'string' ? crm.provider.trim() : '',
    // presence semantics, as everywhere else in the CRM code: only an explicit true switches a
    // gate on. requireLeadID is the server's own walk-in predicate (crm.LeadRequiredBy), and
    // leadRequiredBy names the provider whose link a booking must start from.
    requireLeadID: crm.requireLeadID === true,
    leadRequiredBy: typeof crm.leadRequiredBy === 'string' ? crm.leadRequiredBy.trim() : '',
    features: isObject(crm.features) ? crm.features : {},
    providers,
    // the tenant's list for bookings.sales_source (MS-6628): strings only, blanks and repeats out
    salesSources: Array.isArray(crm.salesSources)
      ? crm.salesSources
        .filter(v => typeof v === 'string' && v.trim() !== '')
        .map(v => v.trim())
        .filter((v, i, all) => all.indexOf(v) === i)
      : [],
  };
}

// salesSourceOptions is the dropdown for bookings.sales_source: the tenant's list, plus any value
// the caller names that is not on the list any more — the booking's saved value, and the one being
// edited — so an older value stays readable and re-selectable throughout an edit rather than
// vanishing from the control that shows it. With no list there is no dropdown at all: a value a
// booking carries on a tenant without a list is text, not a one-entry menu.
export function salesSourceOptions(appConfig, ...values) {
  const list = tenantCRM(appConfig).salesSources.slice();
  if (!list.length) return [];
  values.forEach((v) => {
    const value = String(v || '').trim();
    if (value && list.indexOf(value) < 0) list.unshift(value);
  });
  return list.map(v => ({ key: v, value: v, text: v }));
}

// leadRecordURL is where "open this lead in the CRM" goes for a booking (MS-6772): the provider
// entry's recordURL template from /app/ (book-api builds it from the row's base URL; OutPerform's
// is its mhub_deal_redirect, which answers 302 to the deal page) with {lead_id} replaced by the
// booking's lead id. Empty when the tenant has no template for that provider or the booking has
// no lead id -- the caller then falls back to whatever it showed before, or to nothing.
// leadID must be the CRM's LEAD id (bookings.crm_lead_id), never the banner's display
// reference, which prefers the opportunity reference when the link carried one.
// The finished URL has to parse as absolute https: the template comes from our own API, but a
// mis-set row must produce no button rather than a javascript: or http: link.
export function leadRecordURL(appConfig, provider, leadID) {
  const name = String(provider || '').trim();
  const lead = String(leadID || '').trim();
  if (!name || !lead) return '';
  const entry = tenantCRM(appConfig).providers.find(p => p.provider.trim() === name);
  const template = entry && typeof entry.recordURL === 'string' ? entry.recordURL.trim() : '';
  if (!template || template.indexOf('{lead_id}') < 0) return '';
  const url = template.split('{lead_id}').join(encodeURIComponent(lead));
  try {
    return new URL(url).protocol === 'https:' ? url : '';
  } catch (e) {
    return '';
  }
}

// isOutperformTenant is the gate for the tenant-level OutPerform screens.
export function isOutperformTenant(appConfig) {
  return tenantCRM(appConfig).provider === OUTPERFORM;
}

// isExternalAgencyUser: an external agency's people (agent, agent leader, agency admin). They
// cannot act on a CRM or ERP sync failure and those systems are not theirs, so nothing of the
// integrations is drawn for them (MS-6748, decision 22 Oct). A UI gate only; the server ACL is
// untouched.
export function isExternalAgencyUser(auth) {
  const a = auth || {};
  return Boolean(a.isExternalAgent || a.isExternalAgentLeader || a.isExternalAgencyAdmin);
}

// integrationStatusVisible: who gets the sync tiles and the CRM banner on a booking — an
// OutPerform tenant's own people.
export function integrationStatusVisible(appConfig, auth) {
  return isOutperformTenant(appConfig) && !isExternalAgencyUser(auth);
}

// outperformGateDecision is what a route reserved for OutPerform tenants should do: 'wait' while
// /app/ has not answered yet (the reducer holds {} until then; deciding on that would forbid every
// OP tenant for the first second), 'allow' for an OP tenant, 'forbid' for everyone else.
// What is known about the tenant decides, and a failed read decides only when nothing is known:
// the reducer keeps the last good /app/ answer through a later failed refresh, and an admin on a
// tenant already known to be OutPerform must not be bounced to /forbidden/ by a transient error.
export function outperformGateDecision(appConfig) {
  const data = appConfig && appConfig.data;
  if (!isObject(data) || Object.keys(data).length === 0) {
    // Nothing known: wait for /app/, unless it has failed, in which case there is no answer to wait
    // for and a tenant nothing is known about is not an OutPerform tenant.
    return appConfig && appConfig.isError ? 'forbid' : 'wait';
  }
  return isOutperformTenant(appConfig) ? 'allow' : 'forbid';
}

// providerEntry is the tenant's entry for one provider, or null.
export function providerEntry(appConfig, provider) {
  const key = String(provider || '').trim();
  if (!key) return null;
  return tenantCRM(appConfig).providers.find(p => p.provider === key) || null;
}

// tenantProviderLabel is the name to show for a provider: the admin's display name from the
// tenant's entry when there is one, else the built-in label, else the raw key.
export function tenantProviderLabel(appConfig, provider) {
  const entry = providerEntry(appConfig, provider);
  if (entry && typeof entry.displayName === 'string' && entry.displayName.trim()) return entry.displayName.trim();
  return providerLabel(provider);
}

// walkInGateApplies is whether the server will refuse a walk-in from this user on this tenant: the
// gate flag (the server's own predicate) and an internal branch. Provider-neutral, because the
// server's refusal is: any tenant whose CRM requires a lead refuses, not only OutPerform. The copy
// that must not lie about being able to book by hand reads this; the OutPerform notice below adds
// the ticket's tenant gate on top.
export function walkInGateApplies({ appConfig, auth } = {}) {
  if (!tenantCRM(appConfig).requireLeadID) return false;
  const branch = auth && auth.companyBranch;
  return Boolean(branch) && branch.isExternal === false;
}

// walkInNoticeFor decides whether the booking form should tell the salesperson, before they type
// anything, that a booking here has to start from the CRM (MS-6625's gate, seen from the form).
//
// The server is the gate; this is the courtesy that stops someone filling in forty fields for a
// customer standing in front of them and then meeting a 422. So it says something only when the
// refusal is certain from what the form knows:
//   - this is an OutPerform tenant (the ticket's gate for the walk-in copy: `crm.provider`);
//   - the server says it will refuse (`crm.requireLeadID` is its own predicate, computed over the
//     same rows the create handler reads, so the two cannot disagree);
//   - there is no link session on this form — none arrived, or the one that did was rejected;
//   - the pre-fill is not still running (a session may yet land);
//   - the user is on an internal branch. External agents are exempt from the gate, and a user whose
//     branch is not known is left alone: a wrong warning is worse than the server's own answer.
// Returns null, or { provider, label }: the provider a booking must start from (`leadRequiredBy`,
// which need not be the CRM of record) and the tenant's name for it.
export function walkInNoticeFor({ appConfig, auth, session, pending } = {}) {
  const crm = tenantCRM(appConfig);
  if (crm.provider !== OUTPERFORM || !walkInGateApplies({ appConfig, auth })) return null;
  if (session || pending) return null;
  const provider = crm.leadRequiredBy || crm.provider;
  return { provider, label: tenantProviderLabel(appConfig, provider) };
}



// WEBPACK FOOTER //
// ./src/utils/crmConfig.js